import { SbbDateInputElement } from "@sbb-esta/lyne-elements/date-input.js";
import { SbbTimeInputElement } from "@sbb-esta/lyne-elements/time-input.js";
import { SbbDateInput } from "@sbb-esta/lyne-react/date-input";
import {
  SbbDatepicker,
  SbbDatepickerNextDay,
  SbbDatepickerPreviousDay,
  SbbDatepickerToggle,
} from "@sbb-esta/lyne-react/datepicker";
import { SbbIcon } from "@sbb-esta/lyne-react/icon";
import { SbbTimeInput } from "@sbb-esta/lyne-react/time-input";
import { datePickerPlaceholder } from "ApiConstraints";
import classNames from "classnames";
import { FormField } from "components/Forms/FormField";
import {
  DateFieldValue,
  formatDate,
  formatTime,
  generateGuid,
  parseDateLenient,
  parseLyneDate,
  parseLyneTime,
  setTimePart,
} from "helpers";
import { createLogger } from "logging";
import { ResourceKey } from "providers";
import React, { RefObject, useMemo, useRef } from "react";

import { defaultSize } from "./LyneConstants";
import { LyneFieldError } from "./LyneFieldError";
import { LyneFormField } from "./LyneFormField";
import { TimeSuggestions } from "./TimeSuggestions";
import useTimeInputSuggestions from "./useTimeInputSuggestions";

const logger = createLogger("LyneDateTimePicker");

interface LyneDateTimePickerProps extends FormField {
  format?: string;
  className?: string;
  value: Date | undefined;
  min?: string;
  max?: string;
  placeholder?: string;
  dateError?: string;
  timeError?: string;
  onChange: (value: Date | undefined) => void;
  forwardRef?: RefObject<SbbDateInputElement | SbbTimeInputElement>;
  size?: "s" | "m" | "l";
  timeLabel?: ResourceKey;
}

export const LyneDateTimePicker: React.FC<LyneDateTimePickerProps> = ({
  name,
  className,
  value,
  label,
  timeLabel,
  onChange,
  disabled,
  required,
  size = defaultSize,
  dateError,
  timeError,
  contextHelp,
}) => {
  const nameToUse = useMemo(() => name || generateGuid(), [name]);

  const dateInput = `${nameToUse}-date-input`;
  const datePicker = `${nameToUse}-date-picker`;
  const dateRef = useRef<SbbDateInputElement>(null);
  const dateValue = value;
  const datePart = formatDate(value);
  const dateClassName = classNames(className, required && !datePart ? "sbb-invalid" : "");

  const timeInput = `${nameToUse}-time-input`;
  const timeRef = useRef<SbbTimeInputElement>(null);
  const timePart = formatTime(value);
  const timeClassName = classNames(className, required && !timePart ? "sbb-invalid" : "");

  const handleDateChange = (newDate: DateFieldValue, newTime: string) => {
    logger.debug("onChangeDate(): ", formatDate(newDate), newTime);
    if (!newDate || !newTime) {
      return;
    }

    const newValue = parseDateLenient(newDate);
    setTimePart(newValue, newTime);
    logger.debug(`onChangeDate(): ${newValue} (was: ${value})`);

    onChange(newValue);
  };

  const handleTimeChange = (newDate: DateFieldValue, newTime: string) => {
    logger.debug("onChangeTime(): ", formatDate(newDate), newTime);
    if (!newDate || !newTime) {
      return;
    }

    const newValue = parseDateLenient(newDate);
    setTimePart(newValue, newTime);
    logger.debug(`onChangeTime(): ${newValue} (was: ${value})`);

    onChange(newValue);
  };

  const timeSuggestions = useTimeInputSuggestions(timeRef, (newValue) => {
    const parsed = parseLyneTime(newValue, true);
    handleTimeChange(dateValue, parsed || "");
  });

  return (
    <div className="d-flex justify-content-between">
      <div className="me-3 flex-fill w-66">
        <LyneFormField size={size} name={dateInput} label={label} required={required} contextHelp={contextHelp}>
          <SbbDatepickerPreviousDay input={dateInput} />
          <SbbDateInput
            required
            name={dateInput}
            value={datePart}
            disabled={disabled}
            ref={dateRef}
            className={dateClassName}
            placeholder={datePickerPlaceholder}
            onInput={() => {
              const value = dateRef.current?.value || "";
              const newDate = parseLyneDate(value);
              logger.debug(`${dateInput}.onInput(): value:='${value}' -> '${newDate}'`);
              handleDateChange(newDate, timePart);
            }}
            onFocus={() => {
              logger.debug(`${dateInput}.onFocus(): `);
              dateRef.current?.select();
            }}
            onBlur={() => {
              const value = dateRef.current?.value || "";
              const newDate = parseLyneDate(value, true);
              logger.debug(`${dateInput}.onBlur(): value:='${value}' -> '${newDate}'`);
              handleDateChange(newDate, timePart);
            }}
          />
          <SbbDatepicker input={dateInput} id={datePicker} />
          <SbbDatepickerToggle input={dateInput} datepicker={datePicker} />
          <SbbDatepickerNextDay input={dateInput} />
          <LyneFieldError error={dateError} />
        </LyneFormField>
      </div>

      <div className="flex-fill w-33">
        <LyneFormField size={size} name={timeInput} label={timeLabel} required={required} contextHelp={contextHelp}>
          <SbbTimeInput
            required={required}
            name={timeInput}
            ref={timeRef}
            disabled={disabled}
            value={timePart}
            className={timeClassName}
            placeholder="-"
            onInput={() => {
              const value = timeRef.current?.value || "";
              const newTime = parseLyneTime(value);
              logger.debug(`${timeInput}.onInput(): value:='${value}' -> '${newTime}'`);
              handleTimeChange(dateValue, newTime);
            }}
            onFocus={() => {
              logger.debug(`${timeInput}.onFocus(): `);
              timeRef.current?.select();
              timeSuggestions.openTimePanel();
            }}
            onBlur={() => {
              const value = timeRef.current?.value || "";
              const newTime = parseLyneTime(value, true);
              logger.debug(`${timeInput}.onBlur(): value:='${value}' -> '${newTime}'`);
              handleTimeChange(dateValue, newTime);
              timeSuggestions.closeTimePanel();
            }}
          />
          <TimeSuggestions {...timeSuggestions} value={timePart} />
          <SbbIcon slot="prefix" name="clock-medium" />
          <LyneFieldError error={timeError} />
        </LyneFormField>
      </div>
    </div>
  );
};
